import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, Clock, Edit2, Calendar as CalendarIcon, User, AlertTriangle, Check, X, MapPin, Loader2, Save, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { Calendar } from "../../components/ui/calendar";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { format, parseISO, isSameDay } from "date-fns";
import GoogleCalendarSync from "../../components/GoogleCalendarSync";
import AddToCalendar from "../../components/AddToCalendar";
import ICalFeedManager from "../../components/ICalFeedManager";
import FeatureGate from "../../components/FeatureGate";

const CalendarPage = () => {
  const [calendarData, setCalendarData] = useState({ slots: [], bookings: [], blackouts: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Add slot dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newSlot, setNewSlot] = useState({
    date: null,
    start_time: "09:00",
    end_time: "17:00",
    location: "",
    location_address: "",
    buffer_before: 0,
    buffer_after: 0,
    is_tournament_block: false
  });
  
  // Edit slot dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingSlot, setDeletingSlot] = useState(null);
  
  // Blocked time state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [newBlock, setNewBlock] = useState({
    date: null,
    start_time: "",
    end_time: "",
    reason: "",
    is_recurring: false,
    days_of_week: [],
    end_date: null
  });
  const [blockedTimes, setBlockedTimes] = useState([]);

  useEffect(() => {
    fetchCalendarData();
  }, []);

  const fetchCalendarData = async () => {
    try {
      // Use the enhanced endpoint that includes blocked times
      const response = await axios.get(`${API}/availability/calendar/full`, { withCredentials: true });
      setCalendarData(response.data);
      setBlockedTimes(response.data.blocked_times || []);
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist
      try {
        const response = await axios.get(`${API}/availability/calendar`, { withCredentials: true });
        setCalendarData(response.data);
      } catch (fallbackError) {
        toast.error("Failed to fetch calendar data");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSlot = async () => {
    if (!newSlot.date) {
      toast.error("Please select a date");
      return;
    }

    try {
      await axios.post(
        `${API}/availability/slots`,
        {
          ...newSlot,
          date: format(newSlot.date, "yyyy-MM-dd")
        },
        { withCredentials: true }
      );

      toast.success("Availability slot created!");
      setAddDialogOpen(false);
      setNewSlot({
        date: null,
        start_time: "09:00",
        end_time: "17:00",
        location: "",
        location_address: "",
        buffer_before: 0,
        buffer_after: 0,
        is_tournament_block: false
      });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create slot");
    }
  };

  const openEditDialog = (slot) => {
    setEditingSlot({ ...slot });
    setEditDialogOpen(true);
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/availability/slots/${editingSlot.slot_id}`,
        {
          date: editingSlot.date,
          start_time: editingSlot.start_time,
          end_time: editingSlot.end_time,
          location: editingSlot.location,
          location_address: editingSlot.location_address,
          buffer_before: editingSlot.buffer_before,
          buffer_after: editingSlot.buffer_after
        },
        { withCredentials: true }
      );
      toast.success("Slot updated!");
      setEditDialogOpen(false);
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update slot");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlot = async () => {
    if (!deletingSlot) return;
    
    try {
      await axios.delete(`${API}/availability/slots/${deletingSlot.slot_id}`, { withCredentials: true });
      toast.success("Slot deleted");
      setDeleteDialogOpen(false);
      setDeletingSlot(null);
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete slot");
    }
  };

  // ========== BLOCKED TIME FUNCTIONS ==========
  
  const handleCreateBlock = async () => {
    if (!newBlock.date && !newBlock.is_recurring) {
      toast.error("Please select a date");
      return;
    }
    
    if (newBlock.is_recurring && newBlock.days_of_week.length === 0) {
      toast.error("Please select at least one day of the week");
      return;
    }
    
    if (newBlock.is_recurring && !newBlock.end_date) {
      toast.error("Please select an end date for recurring blocks");
      return;
    }
    
    try {
      if (newBlock.is_recurring) {
        await axios.post(
          `${API}/availability/blocks/recurring`,
          {
            start_time: newBlock.start_time || null,
            end_time: newBlock.end_time || null,
            days_of_week: newBlock.days_of_week,
            start_date: format(newBlock.date || new Date(), "yyyy-MM-dd"),
            end_date: format(newBlock.end_date, "yyyy-MM-dd"),
            reason: newBlock.reason || null
          },
          { withCredentials: true }
        );
        toast.success("Recurring blocked times created!");
      } else {
        await axios.post(
          `${API}/availability/blocks`,
          {
            date: format(newBlock.date, "yyyy-MM-dd"),
            start_time: newBlock.start_time || null,
            end_time: newBlock.end_time || null,
            reason: newBlock.reason || null
          },
          { withCredentials: true }
        );
        toast.success("Blocked time created!");
      }
      
      setBlockDialogOpen(false);
      setNewBlock({
        date: null,
        start_time: "",
        end_time: "",
        reason: "",
        is_recurring: false,
        days_of_week: [],
        end_date: null
      });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create blocked time");
    }
  };
  
  const handleDeleteBlock = async (blockId, recurrenceGroupId = null) => {
    try {
      if (recurrenceGroupId) {
        // Ask if they want to delete all recurring blocks
        const deleteAll = window.confirm("Delete all future blocks in this recurring series?");
        if (deleteAll) {
          await axios.delete(`${API}/availability/blocks/group/${recurrenceGroupId}`, { withCredentials: true });
          toast.success("All recurring blocks deleted");
        } else {
          await axios.delete(`${API}/availability/blocks/${blockId}`, { withCredentials: true });
          toast.success("Block deleted");
        }
      } else {
        await axios.delete(`${API}/availability/blocks/${blockId}`, { withCredentials: true });
        toast.success("Block deleted");
      }
      fetchCalendarData();
    } catch (error) {
      toast.error("Failed to delete block");
    }
  };
  
  const toggleDayOfWeek = (day) => {
    setNewBlock(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };
  
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Group slots by date
  const slotsByDate = calendarData.slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  // Group bookings by date
  const bookingsByDate = calendarData.bookings.reduce((acc, booking) => {
    const date = booking.event_date || (booking.is_tournament ? booking.tournament_start_date : null);
    if (date) {
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(booking);
    }
    return acc;
  }, {});
  
  // Group blocked times by date
  const blocksByDate = blockedTimes.reduce((acc, block) => {
    if (!acc[block.date]) {
      acc[block.date] = [];
    }
    acc[block.date].push(block);
    return acc;
  }, {});

  // Get dates with content for calendar highlighting
  const datesWithSlots = Object.keys(slotsByDate).map(d => new Date(d + "T00:00:00"));
  const datesWithBookings = Object.keys(bookingsByDate).map(d => new Date(d + "T00:00:00"));
  const datesWithBlocks = Object.keys(blocksByDate).map(d => new Date(d + "T00:00:00"));

  const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
  const slotsForSelectedDate = slotsByDate[selectedDateStr] || [];
  const bookingsForSelectedDate = bookingsByDate[selectedDateStr] || [];
  const blocksForSelectedDate = blocksByDate[selectedDateStr] || [];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      needs_info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30"
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Calendar</h1>
          <p className="text-muted-foreground">Manage availability, view requests & bookings</p>
        </div>
        
        <Button 
          className="btn-primary rounded-sm"
          onClick={() => {
            setNewSlot(prev => ({ ...prev, date: selectedDate }));
            setAddDialogOpen(true);
          }}
          data-testid="add-slot-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Availability
        </Button>
        <Button 
          variant="outline"
          onClick={() => {
            setNewBlock(prev => ({ ...prev, date: selectedDate }));
            setBlockDialogOpen(true);
          }}
          data-testid="block-time-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Block Time
        </Button>
      </div>

      <div className="grid lg:grid-cols-[350px_1fr] gap-8">
        {/* Calendar */}
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              modifiers={{ 
                available: datesWithSlots,
                booked: datesWithBookings,
                blocked: datesWithBlocks
              }}
              modifiersStyles={{
                available: {
                  backgroundColor: "hsl(var(--primary) / 0.2)",
                  color: "hsl(var(--primary))"
                },
                booked: {
                  border: "2px solid hsl(var(--primary))",
                  fontWeight: "bold"
                },
                blocked: {
                  backgroundColor: "hsl(0 84% 60% / 0.2)",
                  color: "hsl(0 84% 60%)",
                  textDecoration: "line-through"
                }
              }}
              className="rounded-sm"
              data-testid="availability-calendar"
            />
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-primary/20" />
                <span className="text-muted-foreground">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded border-2 border-primary" />
                <span className="text-muted-foreground">Has Bookings</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-red-500/20" />
                <span className="text-muted-foreground">Blocked</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Day Details */}
        <div className="space-y-6">
          <h2 className="text-xl font-serif">
            {format(selectedDate, "EEEE, MMMM d, yyyy")}
          </h2>
          
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="slots">
                Availability ({slotsForSelectedDate.length})
              </TabsTrigger>
              <TabsTrigger value="bookings">
                Bookings ({bookingsForSelectedDate.length})
              </TabsTrigger>
              <TabsTrigger value="blocked">
                Blocked ({blocksForSelectedDate.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="space-y-4">
              {/* Blocked Times */}
              {blocksForSelectedDate.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-400 mb-2 uppercase tracking-wider">Blocked Times</h3>
                  <div className="space-y-2">
                    {blocksForSelectedDate.map((block) => (
                      <Card key={block.block_id} className="bg-red-500/10 border-red-500/30">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <X className="w-5 h-5 text-red-400" />
                              <div>
                                <p className="font-medium text-red-400">
                                  {block.start_time && block.end_time 
                                    ? `${block.start_time} - ${block.end_time}`
                                    : "All Day"}
                                </p>
                                {block.reason && (
                                  <p className="text-sm text-muted-foreground">{block.reason}</p>
                                )}
                                {block.recurrence_group_id && (
                                  <Badge variant="outline" className="mt-1 text-xs">Recurring</Badge>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => handleDeleteBlock(block.block_id, block.recurrence_group_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Availability Slots */}
              {slotsForSelectedDate.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 uppercase tracking-wider">Availability</h3>
                  <div className="space-y-2">
                    {slotsForSelectedDate.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((slot) => (
                      <SlotCard 
                        key={slot.slot_id} 
                        slot={slot} 
                        onEdit={() => openEditDialog(slot)}
                        onDelete={() => {
                          setDeletingSlot(slot);
                          setDeleteDialogOpen(true);
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Bookings */}
              {bookingsForSelectedDate.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 uppercase tracking-wider">Bookings & Requests</h3>
                  <div className="space-y-2">
                    {bookingsForSelectedDate.sort((a, b) => (a.start_time || "00:00").localeCompare(b.start_time || "00:00")).map((booking) => (
                      <BookingCard key={booking.request_id} booking={booking} />
                    ))}
                  </div>
                </div>
              )}
              
              {slotsForSelectedDate.length === 0 && bookingsForSelectedDate.length === 0 && blocksForSelectedDate.length === 0 && (
                <Card className="bg-card border-border">
                  <CardContent className="py-12 text-center">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">Nothing scheduled for this date</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        setNewSlot(prev => ({ ...prev, date: selectedDate }));
                        setAddDialogOpen(true);
                      }}
                    >
                      Add availability
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="slots" className="space-y-2">
              {slotsForSelectedDate.length > 0 ? (
                slotsForSelectedDate.sort((a, b) => a.start_time.localeCompare(b.start_time)).map((slot) => (
                  <SlotCard 
                    key={slot.slot_id} 
                    slot={slot} 
                    onEdit={() => openEditDialog(slot)}
                    onDelete={() => {
                      setDeletingSlot(slot);
                      setDeleteDialogOpen(true);
                    }}
                  />
                ))
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No availability slots for this date</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="bookings" className="space-y-2">
              {bookingsForSelectedDate.length > 0 ? (
                bookingsForSelectedDate.sort((a, b) => (a.start_time || "00:00").localeCompare(b.start_time || "00:00")).map((booking) => (
                  <BookingCard key={booking.request_id} booking={booking} />
                ))
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No bookings for this date</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            <TabsContent value="blocked" className="space-y-2">
              {blocksForSelectedDate.length > 0 ? (
                blocksForSelectedDate.map((block) => (
                  <Card key={block.block_id} className="bg-red-500/10 border-red-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <X className="w-5 h-5 text-red-400" />
                          <div>
                            <p className="font-medium text-red-400">
                              {block.start_time && block.end_time 
                                ? `${block.start_time} - ${block.end_time}`
                                : "All Day Blocked"}
                            </p>
                            {block.reason && (
                              <p className="text-sm text-muted-foreground">{block.reason}</p>
                            )}
                            {block.recurrence_group_id && (
                              <Badge variant="outline" className="mt-1 text-xs">Part of recurring series</Badge>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDeleteBlock(block.block_id, block.recurrence_group_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="py-8 text-center">
                    <p className="text-muted-foreground">No blocked times for this date</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => {
                        setNewBlock(prev => ({ ...prev, date: selectedDate }));
                        setBlockDialogOpen(true);
                      }}
                    >
                      Block this date
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Summary Section */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-serif">Upcoming Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-serif text-primary">
                    {calendarData.bookings.filter(b => b.status === "pending").length}
                  </p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-serif text-green-400">
                    {calendarData.bookings.filter(b => b.status === "confirmed").length}
                  </p>
                  <p className="text-xs text-muted-foreground">Confirmed</p>
                </div>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-serif">
                    {calendarData.slots.filter(s => !s.is_booked).length}
                  </p>
                  <p className="text-xs text-muted-foreground">Available Slots</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Google Calendar Sync - Pro+ feature */}
          <FeatureGate feature="calendar_sync">
            <GoogleCalendarSync onSyncComplete={fetchCalendarData} />
          </FeatureGate>
          
          {/* iCal Feed (Apple Calendar, Outlook, etc.) - Pro+ feature */}
          <FeatureGate feature="calendar_sync">
            <ICalFeedManager />
          </FeatureGate>
        </div>
      </div>

      {/* Add Slot Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Add Availability Slot</DialogTitle>
            <DialogDescription>
              Create a new time slot when you&apos;re available for bookings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Date</Label>
              <Calendar
                mode="single"
                selected={newSlot.date}
                onSelect={(date) => setNewSlot(prev => ({ ...prev, date }))}
                className="rounded-sm border"
                data-testid="new-slot-date"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Start Time</Label>
                <Input
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, start_time: e.target.value }))}
                  data-testid="new-slot-start"
                />
              </div>
              <div>
                <Label className="mb-2 block">End Time</Label>
                <Input
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, end_time: e.target.value }))}
                  data-testid="new-slot-end"
                />
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Location Name</Label>
              <Input
                value={newSlot.location}
                onChange={(e) => setNewSlot(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., City Sports Complex"
                data-testid="new-slot-location"
              />
            </div>

            <div>
              <Label className="mb-2 block">Location Address</Label>
              <Input
                value={newSlot.location_address}
                onChange={(e) => setNewSlot(prev => ({ ...prev, location_address: e.target.value }))}
                placeholder="e.g., 123 Main St, City, State"
                data-testid="new-slot-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Buffer Before (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSlot.buffer_before}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, buffer_before: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label className="mb-2 block">Buffer After (min)</Label>
                <Input
                  type="number"
                  min="0"
                  value={newSlot.buffer_after}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, buffer_after: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                checked={newSlot.is_tournament_block}
                onCheckedChange={(checked) => setNewSlot(prev => ({ ...prev, is_tournament_block: checked }))}
              />
              <Label>Tournament / Multi-day Block</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSlot} data-testid="create-slot-btn">
              Create Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Slot Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Availability Slot</DialogTitle>
          </DialogHeader>
          
          {editingSlot && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Start Time</Label>
                  <Input
                    type="time"
                    value={editingSlot.start_time}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">End Time</Label>
                  <Input
                    type="time"
                    value={editingSlot.end_time}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Location Name</Label>
                <Input
                  value={editingSlot.location || ""}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., City Sports Complex"
                />
              </div>

              <div>
                <Label className="mb-2 block">Location Address</Label>
                <Input
                  value={editingSlot.location_address || ""}
                  onChange={(e) => setEditingSlot(prev => ({ ...prev, location_address: e.target.value }))}
                  placeholder="e.g., 123 Main St, City, State"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Buffer Before (min)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingSlot.buffer_before || 0}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, buffer_before: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Buffer After (min)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={editingSlot.buffer_after || 0}
                    onChange={(e) => setEditingSlot(prev => ({ ...prev, buffer_after: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSlot} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete Slot
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this availability slot?
            </DialogDescription>
          </DialogHeader>
          
          {deletingSlot && (
            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="font-medium">{deletingSlot.date}</p>
              <p className="text-sm text-muted-foreground">
                {deletingSlot.start_time} - {deletingSlot.end_time}
              </p>
              {deletingSlot.location && (
                <p className="text-sm text-muted-foreground">{deletingSlot.location}</p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSlot}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Block Time Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Block Time</DialogTitle>
            <DialogDescription>
              Block out time when you&apos;re unavailable. This will NOT sync to Google Calendar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Recurring toggle */}
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <Label className="font-medium">Recurring Block</Label>
                <p className="text-xs text-muted-foreground">Block same time on multiple days</p>
              </div>
              <Switch
                checked={newBlock.is_recurring}
                onCheckedChange={(checked) => setNewBlock(prev => ({ ...prev, is_recurring: checked }))}
              />
            </div>
            
            {!newBlock.is_recurring ? (
              <div>
                <Label className="mb-2 block">Date</Label>
                <Calendar
                  mode="single"
                  selected={newBlock.date}
                  onSelect={(date) => setNewBlock(prev => ({ ...prev, date }))}
                  className="rounded-sm border"
                  disabled={(date) => date < new Date()}
                />
              </div>
            ) : (
              <>
                <div>
                  <Label className="mb-2 block">Days of Week</Label>
                  <div className="flex flex-wrap gap-2">
                    {dayNames.map((day, index) => (
                      <Button
                        key={day}
                        type="button"
                        variant={newBlock.days_of_week.includes(index) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleDayOfWeek(index)}
                      >
                        {day}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-2 block">Start Date</Label>
                    <Calendar
                      mode="single"
                      selected={newBlock.date}
                      onSelect={(date) => setNewBlock(prev => ({ ...prev, date }))}
                      className="rounded-sm border text-sm"
                      disabled={(date) => date < new Date()}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block">End Date</Label>
                    <Calendar
                      mode="single"
                      selected={newBlock.end_date}
                      onSelect={(date) => setNewBlock(prev => ({ ...prev, end_date: date }))}
                      className="rounded-sm border text-sm"
                      disabled={(date) => date < (newBlock.date || new Date())}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="p-3 bg-muted/30 rounded-lg">
              <Label className="mb-2 block">Time Window (optional)</Label>
              <p className="text-xs text-muted-foreground mb-3">Leave empty to block the entire day</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Start Time</Label>
                  <Input
                    type="time"
                    value={newBlock.start_time}
                    onChange={(e) => setNewBlock(prev => ({ ...prev, start_time: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">End Time</Label>
                  <Input
                    type="time"
                    value={newBlock.end_time}
                    onChange={(e) => setNewBlock(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label className="mb-2 block">Reason (optional)</Label>
              <Input
                placeholder="e.g., Personal appointment, Vacation, etc."
                value={newBlock.reason}
                onChange={(e) => setNewBlock(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBlock} className="bg-red-600 hover:bg-red-700">
              <X className="w-4 h-4 mr-2" />
              {newBlock.is_recurring ? "Create Recurring Blocks" : "Block Time"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Slot Card Component
const SlotCard = ({ slot, onEdit, onDelete }) => (
  <Card className={`bg-card border-border ${slot.is_booked ? 'opacity-60' : ''}`}>
    <CardContent className="p-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-sm ${slot.is_booked ? 'bg-muted' : 'bg-primary/10'}`}>
          <Clock className={`w-5 h-5 ${slot.is_booked ? 'text-muted-foreground' : 'text-primary'}`} />
        </div>
        <div>
          <p className="font-medium">
            {slot.start_time} - {slot.end_time}
          </p>
          {slot.location && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 cursor-help">
                    <MapPin className="w-3 h-3" />
                    {slot.location}
                  </p>
                </TooltipTrigger>
                {slot.location_address && (
                  <TooltipContent>
                    <p>{slot.location_address}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="flex items-center gap-2 mt-1">
            {slot.is_booked ? (
              <Badge variant="outline" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                Booked
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs">Available</Badge>
            )}
            {slot.is_tournament_block && (
              <Badge variant="outline" className="text-xs text-yellow-400 border-yellow-500/30">
                Tournament
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {!slot.is_booked && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300"
            onClick={onDelete}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);

// Booking Card Component
const BookingCard = ({ booking }) => {
  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
      declined: "bg-red-500/20 text-red-400 border-red-500/30",
      needs_info: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-purple-500/20 text-purple-400 border-purple-500/30"
    };
    return styles[status] || styles.pending;
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-sm ${
              booking.status === "confirmed" ? "bg-green-500/10" : "bg-yellow-500/10"
            }`}>
              <User className={`w-5 h-5 ${
                booking.status === "confirmed" ? "text-green-400" : "text-yellow-400"
              }`} />
            </div>
            <div>
              <p className="font-medium">{booking.customer_name}</p>
              <p className="text-sm text-muted-foreground">
                {booking.event_type} • {booking.request_types?.join(", ")}
              </p>
              {(booking.start_time || booking.end_time) && (
                <p className="text-sm text-muted-foreground">
                  {booking.start_time} - {booking.end_time}
                </p>
              )}
              {booking.venue_name && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {booking.venue_name}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={`${getStatusBadge(booking.status)} border`}>
              {booking.status.replace("_", " ")}
            </Badge>
            {booking.status === "confirmed" && booking.event_date && (
              <AddToCalendar
                title={`📸 ${booking.event_type}: ${booking.customer_name}`}
                description={`Client: ${booking.customer_name}\nType: ${booking.event_type}\nServices: ${booking.request_types?.join(', ')}`}
                location={booking.venue_name || ''}
                startDate={booking.event_date}
                startTime={booking.start_time || "09:00"}
                endTime={booking.end_time || "17:00"}
                size="xs"
              />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarPage;
